import { useState, useEffect } from 'react';
import { Trophy, Flame, TrendingUp, Activity, Heart, BookHeart, Wind, MessageCircle, Pill } from 'lucide-react';
import { ACHIEVEMENTS, MOODS } from '../lib/constants';
import { getMoodLog, getSessionsForPatient, getJournalEntries, getBreathingLog, getStreak } from '../lib/mock';
import EmotionalMap from '../components/EmotionalMap';

const ICON_MAP = { Flame, Trophy, Wind, BookHeart, Pill, MessageCircle };

export default function JourneyScreen({ patient }) {
  const [moodLog, setMoodLog] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [journal, setJournal] = useState([]);
  const [breath, setBreath] = useState([]);
  const [streak, setStreak] = useState(0);

  useEffect(() => {
    (async () => {
      const ml = await getMoodLog(patient.id);
      setMoodLog(ml); setStreak(getStreak(ml));
      setSessions(await getSessionsForPatient(patient.id));
      setJournal(await getJournalEntries(patient.id));
      setBreath(await getBreathingLog(patient.id));
    })();
  }, []);

  // Build weekly chart (last 7 days)
  const days = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(); d.setDate(d.getDate() - i);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const found = moodLog.find(m => m.date === key);
    days.push({
      key, label: ['أحد', 'إثنين', 'ثلاثاء', 'أربعاء', 'خميس', 'جمعة', 'سبت'][d.getDay()],
      day: d.getDate(),
      mood: found,
      score: found?.score || 0
    });
  }

  const avgScore = moodLog.length > 0 ? (moodLog.slice(0, 7).reduce((a, m) => a + (m.score || 0), 0) / Math.min(moodLog.length, 7)).toFixed(1) : 0;

  // Compute unlocked achievements
  const unlocked = new Set();
  if (sessions.length >= 1) unlocked.add('first_session');
  if (streak >= 3) unlocked.add('streak_3');
  if (streak >= 7) unlocked.add('streak_7');
  if (breath.length >= 5) unlocked.add('breathing_5');
  if (journal.length >= 10) unlocked.add('journal_10');

  return (
    <div className="px-4 pt-4 pb-6 max-w-2xl mx-auto">
      <div className="mb-4">
        <h2 className="text-xl font-extrabold" style={{ color: 'var(--text)' }}>رحلتي</h2>
        <p className="text-[12.5px]" style={{ color: 'var(--text-2)' }}>تتبّعي تقدمك واحتفلي بإنجازاتك</p>
      </div>

      {/* Monthly emotional map */}
      <EmotionalMap patient={patient} />

      {/* Streak card */}
      <div className="rounded-3xl p-4 mb-4 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #f0c674, #f5b892)' }}>
        <div className="absolute -top-4 -right-4 w-24 h-24 rounded-full bg-white/20 blur-2xl"></div>
        <div className="relative flex items-center gap-3">
          <div className="w-14 h-14 rounded-2xl bg-white/25 backdrop-blur flex items-center justify-center">
            <Flame className="w-7 h-7 text-white" fill="white" />
          </div>
          <div className="flex-1">
            <div className="text-white text-[11.5px] font-bold opacity-90">سلسلة الاستمرارية</div>
            <div className="text-white text-3xl font-extrabold leading-none">{streak} <span className="text-lg">يوم</span></div>
            <div className="text-white text-[10.5px] opacity-90 mt-1">استمرّي — كل يوم مهم</div>
          </div>
        </div>
      </div>

      {/* Weekly mood chart */}
      <div className="card-soft p-4 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'var(--primary-bg)' }}>
            <Heart className="w-3.5 h-3.5" style={{ color: 'var(--primary-dark)' }} fill="var(--primary)" />
          </div>
          <div className="flex-1">
            <div className="text-[13px] font-extrabold" style={{ color: 'var(--text)' }}>مزاجك هذا الأسبوع</div>
            <div className="text-[10.5px]" style={{ color: 'var(--text-2)' }}>متوسط: {avgScore}/6</div>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-1.5 h-32">
          {days.map((d, i) => {
            const height = d.score ? `${(d.score / 6) * 100}%` : '6%';
            const color = d.mood ? MOODS.find(m => m.code === d.mood.code)?.color : 'var(--border)';
            return (
              <div key={i} className="flex flex-col items-center justify-end gap-1">
                <div className="flex-1 flex flex-col justify-end w-full">
                  <div className="w-full rounded-t-lg transition-all duration-500" style={{ height, background: color || 'var(--border-soft)', minHeight: '4px' }}></div>
                </div>
                <div className="text-[9.5px] font-bold" style={{ color: 'var(--text)' }}>{d.mood?.emoji || '·'}</div>
                <div className="text-[8.5px]" style={{ color: 'var(--text-2)' }}>{d.label.slice(0, 2)}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <StatBox icon={MessageCircle} value={sessions.length} label="جلسة" color="#8b7ff0" bg="var(--primary-bg)" />
        <StatBox icon={Wind} value={breath.length} label="تنفس" color="#5eb2a3" bg="var(--secondary-bg)" />
        <StatBox icon={BookHeart} value={journal.length} label="يومية" color="#7dcfa9" bg="var(--sage-bg)" />
      </div>

      {/* Achievements */}
      <div className="card-soft p-4 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'var(--honey-bg)' }}>
            <Trophy className="w-3.5 h-3.5" style={{ color: '#c9871f' }} />
          </div>
          <div className="flex-1">
            <div className="text-[13px] font-extrabold" style={{ color: 'var(--text)' }}>الإنجازات</div>
            <div className="text-[10.5px]" style={{ color: 'var(--text-2)' }}>{unlocked.size}/{ACHIEVEMENTS.length} تم فتحها</div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {ACHIEVEMENTS.map(a => {
            const isUnlocked = unlocked.has(a.id);
            const Icon = ICON_MAP[a.icon] || Trophy;
            return (
              <div key={a.id} className={`rounded-2xl p-2.5 text-center transition-all ${isUnlocked ? '' : 'opacity-40 grayscale'}`} style={{ background: isUnlocked ? `${a.color}18` : 'var(--surface-2)', border: `1.5px solid ${isUnlocked ? a.color + '55' : 'var(--border-soft)'}` }}>
                <div className="w-9 h-9 rounded-xl mx-auto mb-1.5 flex items-center justify-center" style={{ background: `${a.color}25` }}>
                  <Icon className="w-4 h-4" style={{ color: a.color }} fill={isUnlocked ? a.color : 'none'} />
                </div>
                <div className="text-[10.5px] font-extrabold leading-tight" style={{ color: 'var(--text)' }}>{a.label}</div>
                <div className="text-[9px] mt-0.5 leading-tight" style={{ color: 'var(--text-2)' }}>{a.desc}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function StatBox({ icon: Icon, value, label, color, bg }) {
  return (
    <div className="card-soft p-3 text-center">
      <div className="w-8 h-8 rounded-xl mx-auto flex items-center justify-center mb-1.5" style={{ background: bg }}>
        <Icon className="w-3.5 h-3.5" style={{ color }} />
      </div>
      <div className="text-xl font-extrabold" style={{ color }}>{value}</div>
      <div className="text-[10.5px] font-semibold" style={{ color: 'var(--text-2)' }}>{label}</div>
    </div>
  );
}
